import React, { useContext, useState, useCallback, useMemo } from "react";

import { Filter } from "@usereactify/search";

import { FilterContext } from "~/providers/FilterProvider";
import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

import { Sheet } from "~/components/Sheet";

import { FilterSection } from "./FilterSection";
import { FilterSectionListSelector } from "./FilterSectionListSelector";
import { FilterSectionRange } from "./FilterSectionRange";
import { FilterSectionSelectedFilters } from "./FilterSectionSelectedFilters";
import { FilterSectionSort } from "./FilterSectionSort";
import { FilterSectionProductStats } from "./FilterSectionProductStats";
import { FilterSectionClearAll } from "./FilterSectionClearAll";

import { Button, useProductImage, IconFilter } from "@bared/ui";

export const FiltersDesktop = () => {
  const {
    filters,
    sortOption,
    sortOptions,
    setSortOption,
    showViewToggle,
    filtersSelected,
  } = useContext(FilterContext);

  const googleTagManager = useGoogleTagManager();

  const { showHoverImages, toggleHoverImages } = useProductImage();

  const toggleHoverImageLabel = showHoverImages ? "Product View" : "Model View";

  const onToggleHoverImage = useCallback(() => {
    toggleHoverImages();

    googleTagManager.push({
      event: "Button Clicked",
      properties: {
        label: toggleHoverImageLabel,
      },
    });
  }, [toggleHoverImages, googleTagManager, toggleHoverImageLabel]);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const handleSheetToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSectionToggle = useCallback((handle: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(handle)) {
        newSet.delete(handle);
      } else {
        newSet.add(handle);
      }
      return newSet;
    });
  }, []);

  const totalFilterSelectedCount = useMemo(() => {
    return filtersSelected.reduce((total, filter) => {
      if (!filter.value) return total;

      if (Array.isArray(filter.value)) {
        return total + filter.value.length;
      }

      return total + 1;
    }, 0);
  }, [filtersSelected]);

  return (
    <>
      <div className="sticky top-[--header-height] z-40 flex justify-between bg-neutral p-mini md:p-midi">
        <Button
          variant="plain"
          size="small"
          onClick={handleSheetToggle}
          className="flex items-center gap-micro text-hint font-medium leading-hint hover:text-secondary"
        >
          <IconFilter className="w-[10px]" />
          {totalFilterSelectedCount > 0
            ? `Filters & Sort (${totalFilterSelectedCount})`
            : "Filters & Sort"}
        </Button>

        {showViewToggle && (
          <Button
            variant="secondary"
            size="xsmall"
            className="p-0 z-50 w-[95px]"
            onClick={onToggleHoverImage}
            aria-label="Toggle hover images"
          >
            {toggleHoverImageLabel}
          </Button>
        )}
      </div>

      <Sheet
        isOpen={isOpen}
        onClose={handleSheetToggle}
        eventTitle="Filters & Sort"
        layout="condensed"
        title="Filters & Sort"
        hideMode="css"
        className="flex flex-col"
      >
        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1 overflow-y-auto">
            <FilterSectionSort
              show={openSections.has("sort")}
              onClose={() => handleSectionToggle("sort")}
              sortOption={sortOption}
              sortOptions={sortOptions}
              setSortOption={setSortOption}
              scrollTo={() => {}}
            />
            {filters.map((filter) => {
              return (
                <FilterSection
                  show={openSections.has(filter.handle)}
                  onClose={() => handleSectionToggle(filter.handle)}
                  title={filter.name}
                  handle={filter.handle}
                  key={filter.id}
                >
                  <Filter
                    filter={filter}
                    renderFilterList={({ filterListProps }) => {
                      return <FilterSectionListSelector {...filterListProps} />;
                    }}
                    renderFilterRange={({ filterRangeProps }) => (
                      <FilterSectionRange
                        options={filterRangeProps.options}
                        handleChange={filterRangeProps.handleChange}
                      />
                    )}
                    showEmptyFilters={true}
                  />
                </FilterSection>
              );
            })}
          </div>

          <div className="w-full space-y-midi border-t border-primary-300 px-mini py-midi">
            <FilterSectionSelectedFilters layout="scroll" />
            <Button
              variant="primary"
              size="large"
              onClick={handleApplyFilters}
              className="w-full"
            >
              Apply Filters
            </Button>
            <div className="flex items-center justify-between">
              <div>
                <FilterSectionClearAll />
              </div>
              <div>
                <FilterSectionProductStats />
              </div>
            </div>
          </div>
        </div>
      </Sheet>
    </>
  );
};

export default FiltersDesktop;
